/* ==========================================================================
   Satjeevan — Bestsellers Section JS (sj-bestsellers.js)

   Responsibilities:
     1. Manual drag/swipe horizontal scrolling for the card row (desktop
        mouse drag only — touch devices already get native swipe via
        `overflow-x: auto` + `-webkit-overflow-scrolling: touch`).
     2. Instant client-side "Size" variant switching per card: updates
        price, compare-at price, product image, hidden variant id input,
        availability, and the Size/Weight/Quantity label — all without a
        network request, since every variant's data is already embedded
        as JSON at render time.

   Adding to cart itself is handled entirely by Horizon's existing
   <product-form-component> / <add-to-cart-component> custom elements
   (assets/product-form.js, loaded globally) — this file never touches
   /cart/add.js directly, avoiding any duplicate cart logic.
   ========================================================================== */

(function () {
  'use strict';

  var SJ_DRAG_INIT_ATTR = 'data-sj-bsc-drag-init';
  var SJ_VARIANT_INIT_ATTR = 'data-sj-bsc-variant-init';

  /* -----------------------------------------------------------------------
     Size / Weight / Quantity label detection — mirrors the server-side
     Liquid logic in snippets/sj-bestsellers-card.liquid exactly.
     ----------------------------------------------------------------------- */
  function detectUnitLabel(value) {
    var v = (value || '').toLowerCase();
    if (v.indexOf('ml') !== -1) return 'Size';
    if (v.indexOf('kg') !== -1 || v.indexOf('gm') !== -1 || v.indexOf('g') !== -1) return 'Weight';
    return 'Quantity';
  }

  /* -----------------------------------------------------------------------
     1. Manual drag-to-scroll for the bestsellers row
     ----------------------------------------------------------------------- */
  function initDragScroll() {
    var tracks = document.querySelectorAll('.sj-bsc-scroll:not([' + SJ_DRAG_INIT_ATTR + '])');

    tracks.forEach(function (track) {
      track.setAttribute(SJ_DRAG_INIT_ATTR, '1');

      var isDown = false;
      var startX = 0;
      var startScroll = 0;
      var moved = false;

      track.addEventListener('mousedown', function (e) {
        // Prevent the browser's native drag-ghost gesture (links/images) from
        // fighting the custom mousemove-based scroll below. Form controls are
        // excluded so quantity-input focus and button/chip clicks still work.
        if (!e.target.closest('input, textarea, select, button')) {
          e.preventDefault();
        }
        isDown = true;
        moved = false;
        track.classList.add('dragging');
        startX = e.pageX;
        startScroll = track.scrollLeft;
      });

      window.addEventListener('mouseup', function () {
        isDown = false;
        track.classList.remove('dragging');
      });

      window.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var dx = e.pageX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startScroll - dx;
      });

      // Prevent chip/button clicks from firing right after a drag gesture
      track.addEventListener(
        'click',
        function (e) {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    });
  }

  /* -----------------------------------------------------------------------
     2. Size variant switching
     ----------------------------------------------------------------------- */
  function initVariantSwitching() {
    var cards = document.querySelectorAll(
      '[data-sj-bsc-card][data-has-size-option="true"]:not([' + SJ_VARIANT_INIT_ATTR + '])'
    );

    cards.forEach(function (card) {
      card.setAttribute(SJ_VARIANT_INIT_ATTR, '1');

      var variantsScript = card.querySelector('[data-bsc-variants]');
      if (!variantsScript) return;

      /** @type {Array<Object>} */
      var variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (err) {
        return;
      }

      var chips = card.querySelectorAll('[data-bsc-chip]');
      var priceEl = card.querySelector('[data-bsc-price]');
      var compareEl = card.querySelector('[data-bsc-compare]');
      var labelEl = card.querySelector('[data-bsc-label]');
      var imageEl = card.querySelector('.sj-bsc-image');
      var variantInput = card.querySelector('[data-bsc-variant-input]');
      var atcButton = card.querySelector('.sj-bsc-atc-btn');
      var atcTextNode = atcButton
        ? atcButton.querySelector('.add-to-cart-text__content span span')
        : null;

      var textAddToCart = card.getAttribute('data-text-add-to-cart') || 'Add to cart';
      var textSoldOut = card.getAttribute('data-text-sold-out') || 'Sold out';

      function applyVariant(variant, chipEl) {
        if (!variant) return;

        if (priceEl) priceEl.textContent = variant.price;

        var showCompare = Number(variant.compareRaw) > Number(variant.priceRaw);
        if (compareEl) {
          if (showCompare) {
            compareEl.textContent = variant.compare;
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.textContent = '';
            compareEl.setAttribute('hidden', '');
          }
        }

        if (variantInput) variantInput.value = variant.id;

        if (imageEl && variant.image) {
          imageEl.src = variant.image;
          imageEl.removeAttribute('srcset');
        }

        if (labelEl && chipEl) {
          labelEl.textContent = detectUnitLabel(chipEl.textContent);
        }

        chips.forEach(function (c) {
          var isSelected = c === chipEl;
          c.classList.toggle('is-selected', isSelected);
          c.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        if (atcButton) {
          atcButton.disabled = !variant.available;
        }
        if (atcTextNode) {
          atcTextNode.textContent = variant.available ? textAddToCart : textSoldOut;
        }
      }

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var variantId = chip.getAttribute('data-variant-id');
          var variant = null;
          for (var i = 0; i < variants.length; i++) {
            if (String(variants[i].id) === String(variantId)) {
              variant = variants[i];
              break;
            }
          }
          applyVariant(variant, chip);
        });
      });
    });
  }

  /* -----------------------------------------------------------------------
     Bootstrap
     ----------------------------------------------------------------------- */
  function init() {
    initDragScroll();
    initVariantSwitching();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor live-reload support
  document.addEventListener('shopify:section:load', init);
})();
